// @flow
import * as React from 'react';

import clsx from 'clsx';
import { useLocation } from 'react-router-dom';

import { selectHelpdeskUrl } from '@gandi/authentication';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { TechnicalPoints } from 'src/DomainCreate/components/TechnicalPoints';

import { LegalLinks } from 'src/Layout/components/LegalLinks';
import { LocaleSelector } from 'src/Layout/components/LocaleSelector';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useCartTrayVisible } from 'src/hooks/useCartTrayVisible';
import { useLayoutEffect } from 'src/hooks/useLayoutEffect';

import styles from './Footer.scss';

type Props = {
  showCart2Column?: boolean,
};

function useMarginBottom() {
  const [marginBottom, setMarginBottom] = React.useState<?number>();

  const updateMarginBottom = React.useCallback(() => {
    if (typeof document !== 'undefined') {
      const buttonContainer = document.getElementById('cartSummaryButton');
      if (
        document.documentElement &&
        document.documentElement.clientWidth < 768 &&
        buttonContainer
      ) {
        setMarginBottom(buttonContainer.clientHeight);
      } else {
        setMarginBottom(undefined);
      }
    }
  }, []);

  useLayoutEffect(() => {
    updateMarginBottom();
  }, [updateMarginBottom]);

  React.useEffect(() => {
    window.addEventListener('resize', updateMarginBottom);
    return () => {
      window.removeEventListener('resize', updateMarginBottom);
    };
  }, [updateMarginBottom]);

  return { marginBottom };
}

export default function Footer({ showCart2Column }: Props): React.MixedElement {
  const { __ } = useTranslator();
  const location = useLocation();
  const showTechnicalPoints = location.pathname.includes('domain/suggest');
  const supportURL = useAppSelector(selectHelpdeskUrl);

  const { cartTrayDisplayed } = useCartTrayVisible();
  const { marginBottom } = useMarginBottom();

  return (
    <Base
      tagName="footer"
      flexExpandTop
      className={clsx(
        styles.root,
        cartTrayDisplayed ? styles.showCartTray : undefined,
        showCart2Column ? styles.showCart2Column : undefined,
      )}
      style={showCart2Column ? { marginBottom } : undefined}
    >
      {showTechnicalPoints && <TechnicalPoints />}
      <Base
        role="contentinfo"
        flex
        flexJustifyCenter
        flexAlignItemsCenter
        flexExpandTop
        paddingY="xsmall"
        paddingX="small"
        className={styles.navRoot}
      >
        <nav className={styles.nav}>
          <Base
            interactive
            flex
            flexAlignItemsCenter
            flexJustifyCenter
            paddingX="xsmall"
            tagName="a"
            color="base"
            weight="regular"
            href={supportURL}
            className={styles.link}
          >
            {__('Help')}
          </Base>
          <LocaleSelector />
          <LegalLinks />
        </nav>
      </Base>
    </Base>
  );
}
